import { Button, Layout, Typography } from "matsuri-ui"
import {
    ChangeEvent,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import { css } from "@emotion/react"
import { useNavigate, useParams } from "react-router-dom"
import styled from "@emotion/styled"
import { useCleaningDetail } from "./providers/cleaningDetail"
import { Errand } from "@/model/cleaningInfo"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { MobilePageLayout } from "@/components/Layout/MobilePageLayout"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningInfoPagePath } from "@/routes/protected"
import { imageCompressToWebp } from "@/utils/image"
import TakePhotoIcon from "@/components/Icon/TakePhoto.svg"
import { useTranslation } from "@/hooks/useTranslation"

const Thumbnail = (props: React.ComponentPropsWithoutRef<"img">) => {
    return (
        <img
            width="100%"
            height={350}
            css={css`
                object-fit: contain;
            `}
            {...props}
        />
    )
}

const Styled = {
    Cover: styled.div`
        display: flex;
        justify-content: center;
        & > div {
            background-color: rgba(65, 65, 65, 0.8);
            width: 100%;
            height: calc(70vh - 4rem);

            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            & > p {
                color: white;
            }

            & > img {
                width: 92px;
                height: auto;
                margin: 1rem;
            }
        }
    `
}

const Container = styled.div`
    margin-right: 1em;
    margin-left: 1em;
    margin-top: 50px;
`

const sx = {
    spacingBotton: {
        marginBottom: "1em"
    }
}

export const ErrandTakePhotoPage = () => {
    const { t } = useTranslation()
    const { detail, uploadErrandPicture } = useCleaningDetail()
    const errands = useMemo<Record<string, Errand>>(() => {
        return (detail?.errands ?? []).reduce(
            (prev, curr) => ({ ...prev, [curr.id]: curr }),
            {}
        )
    }, [detail])

    const navigate = useNavigate()
    const [photo, setPhoto] = useState<Blob | undefined>(undefined)
    const [pending, setPending] = useState(false)
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)

    const { id } = useParams<"id">()

    const photoObjectUrl = useMemo(
        () => (photo ? URL.createObjectURL(photo) : undefined),
        [photo]
    )
    useEffect(() => {
        return () => {
            if (photoObjectUrl) URL.revokeObjectURL(photoObjectUrl)
        }
    }, [photoObjectUrl])

    const handleSubmit = useCallback(async () => {
        if (id === undefined || !photo) {
            return
        }
        setPending(true)
        try {
            await uploadErrandPicture({ errandId: id, blob: photo })
            navigate(genCleaningInfoPagePath(cleaningId, "errand"))
        } finally {
            setPending(false)
        }
    }, [cleaningId, id, navigate, photo, uploadErrandPicture])

    const inputRef = useRef<HTMLInputElement>(null)

    const handleBack = useCallback(
        (e: SyntheticEvent<HTMLElement>) => {
            e.preventDefault()
            navigate(-1)
        },
        [navigate]
    )

    const handleInputFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        const fileList = e.target.files
        const target = fileList?.[0]
        if (!target) return

        imageCompressToWebp(target).then(blob => {
            setPhoto(blob)
        })
    }, [])

    const handleReset = useCallback(() => {
        setPhoto(undefined)
    }, [])

    const notHasPhoto = !photoObjectUrl

    const errand = id && errands[id]

    return errand ? (
        <MobilePageLayout>
            <Header
                style={{
                    paddingTop: "env(safe-area-inset-top, 0px)"
                }}
            >
                <MobileAppHeader title={t("お願い事撮影")} />
            </Header>
            <Container>
                <Layout.Column gap={2} nowrap>
                    {notHasPhoto ? (
                        <div>
                            <Typography
                                align="center"
                                variant={"h4"}
                                style={sx.spacingBotton}
                            >
                                {t("お願いごとの内容を撮影してください")}
                            </Typography>
                            <Typography align={"center"}>
                                {errand.title}
                            </Typography>
                        </div>
                    ) : (
                        <div>
                            <Typography
                                variant={"h2"}
                                align="center"
                                style={sx.spacingBotton}
                            >
                                {pending
                                    ? t("送信中です...")
                                    : t("こちらでよろしいですか？")}
                            </Typography>
                            <Typography variant={"body"} align="center">
                                {pending
                                    ? t(
                                          "送信が完了すると、お願いごと一覧にリダイレクトします。"
                                      )
                                    : errand.description}
                            </Typography>
                        </div>
                    )}
                    {notHasPhoto ? (
                        <label htmlFor="image-input">
                            <Styled.Cover>
                                <div>
                                    <img src={TakePhotoIcon} />
                                    <Typography>{t("写真を撮影")}</Typography>
                                </div>
                            </Styled.Cover>
                            <input
                                style={{ display: "none" }}
                                id="image-input"
                                ref={inputRef}
                                onChange={handleInputFile}
                                type="file"
                                accept="image/*"
                            />
                        </label>
                    ) : (
                        <div>
                            <Thumbnail src={photoObjectUrl} />
                        </div>
                    )}

                    {notHasPhoto ? (
                        <div>
                            <Button onClick={handleBack} fullWidth>
                                {t("戻る")}
                            </Button>
                        </div>
                    ) : (
                        <Layout.Column gap={2} nowrap>
                            <div>
                                <Button
                                    disabled={pending}
                                    variant="filled"
                                    color="primary"
                                    fullWidth
                                    onClick={handleSubmit}
                                >
                                    OK
                                </Button>
                            </div>
                            <div>
                                <Button
                                    fullWidth
                                    onClick={handleReset}
                                    disabled={pending}
                                >
                                    {t("撮り直し")}
                                </Button>
                            </div>
                        </Layout.Column>
                    )}
                </Layout.Column>
            </Container>
        </MobilePageLayout>
    ) : null
}
